import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  Paper,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Tabs,
  Tab
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { medicineService, adviceService } from '../../services/api';
import TopBar from '../../components/TopBar';

const emptyForm = { name: '', composition: '', dosage: '', description: '', hotkey: '', text: '', hindiText: '' };

const MedicineManagement = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [medicines, setMedicines] = useState([]);
  const [advices, setAdvices] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [medRes, advRes] = await Promise.all([
        medicineService.getAllMedicines(),
        adviceService.getAllAdvices()
      ]);
      setMedicines(medRes.data);
      setAdvices(advRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name || '',
        composition: item.composition || '',
        dosage: item.dosage || '',
        description: item.description || '',
        hotkey: item.hotkey || '',
        text: item.text || '',
        hindiText: item.hindiText || '',
      });
    } else {
      setEditingItem(null);
      setFormData(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (tabValue === 0) {
        // Medicine
        const medicineData = { name: formData.name, composition: formData.composition, dosage: formData.dosage, description: formData.description, hotkey: formData.hotkey };
        if (editingItem) {
          await medicineService.updateMedicine(editingItem.id, { id: editingItem.id, ...medicineData });
        } else {
          await medicineService.createMedicine(medicineData);
        }
      } else {
        // Advice
        const adviceData = { text: formData.text, hotkey: formData.hotkey, hindiText: formData.hindiText || null };
        if (editingItem) {
          await adviceService.updateAdvice(editingItem.id, { id: editingItem.id, ...adviceData });
        } else {
          await adviceService.createAdvice(adviceData);
        }
      }
      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Failed to save:', err);
    }
  };

  const handleDeleteMedicine = async (id) => {
    if (window.confirm('Are you sure you want to delete this medicine?')) {
      try {
        await medicineService.deleteMedicine(id);
        loadData();
      } catch (err) {
        console.error('Failed to delete medicine:', err);
      }
    }
  };

  const handleDeleteAdvice = async (id) => {
    if (window.confirm('Are you sure you want to delete this advice?')) {
      try {
        await adviceService.deleteAdvice(id);
        loadData();
      } catch (err) {
        console.error('Failed to delete advice:', err);
      }
    }
  };

  const actionCell = (onEdit, onDelete) => ({
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    sortable: false,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <IconButton size="small" color="primary" onClick={() => onEdit(params.row)} title="Edit"><Edit fontSize="small" /></IconButton>
        <IconButton size="small" color="error" onClick={() => onDelete(params.row.id)} title="Delete"><Delete fontSize="small" /></IconButton>
      </Box>
    ),
  });

  const medicineCols = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Medicine Name', width: 200 },
    { field: 'composition', headerName: 'Composition', width: 200 },
    { field: 'dosage', headerName: 'Dosage', width: 120 },
    { field: 'description', headerName: 'Description', width: 200 },
    { field: 'hotkey', headerName: 'Hotkey', width: 100 },
    actionCell(handleOpenDialog, handleDeleteMedicine),
  ];

  const adviceCols = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'text', headerName: 'Advice (English)', width: 320 },
    { field: 'hindiText', headerName: 'Advice (Hindi)', width: 280, renderCell: (p) => p.value || '—' },
    { field: 'hotkey', headerName: 'Hotkey', width: 100 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" color="primary" onClick={() => handleOpenDialog(params.row)} title="Edit">
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Medicine & Advice Management" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            sx={{ mb: 3 }}
          >
            <Tab label="Medicines" />
            <Tab label="Advice" />
          </Tabs>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Add {tabValue === 0 ? 'Medicine' : 'Advice'}
            </Button>
          </Box>

          {tabValue === 0 && (
            <DataGrid
              rows={medicines}
              columns={medicineCols}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              autoHeight
              disableSelectionOnClick
            />
          )}
          {tabValue === 1 && (
            <DataGrid
              rows={advices}
              columns={adviceCols}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              autoHeight
              disableSelectionOnClick
            />
          )}
        </Paper>
      </Container>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingItem
            ? `Edit ${tabValue === 0 ? 'Medicine' : 'Advice'}`
            : `Add ${tabValue === 0 ? 'Medicine' : 'Advice'}`
          }
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              {tabValue === 0 && (
                <>
                  {editingItem && (
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="ID" value={editingItem.id} disabled InputProps={{ readOnly: true }} />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={editingItem ? 6 : 12}>
                    <TextField fullWidth label="Medicine Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required disabled={!!editingItem} InputProps={{ readOnly: !!editingItem }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Composition" value={formData.composition} onChange={(e) => setFormData({...formData, composition: e.target.value})} placeholder="e.g., Paracetamol 500mg" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Dosage" value={formData.dosage} onChange={(e) => setFormData({...formData, dosage: e.target.value})} placeholder="e.g., 1-0-1" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} multiline rows={3} placeholder="Additional information about the medicine" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Hotkey (e.g., P)" value={formData.hotkey} onChange={(e) => setFormData({...formData, hotkey: e.target.value})} inputProps={{ maxLength: 5 }} />
                  </Grid>
                </>
              )}
              {tabValue === 1 && (
                <>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Advice Text (English)" value={formData.text} onChange={(e) => setFormData({...formData, text: e.target.value})} required multiline rows={2} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Advice Text (Hindi) — optional"
                      value={formData.hindiText}
                      onChange={(e) => setFormData({...formData, hindiText: e.target.value})}
                      multiline
                      rows={2}
                      placeholder="à¤¹à¤¿à¤‚à¤¦à¥€ à¤…à¤¨à¥à¤µà¤¾à¤¦ à¤¯à¤¹à¤¾à¤ à¤¦à¤°à¥à¤œ à¤•à¤°à¥‡à¤‚"
                      inputProps={{ lang: 'hi' }}
                      helperText="Shown on prescription when patient's Hindi Prescription flag is ON"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Hotkey (e.g., R)" value={formData.hotkey} onChange={(e) => setFormData({...formData, hotkey: e.target.value})} inputProps={{ maxLength: 5 }} />
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingItem ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default MedicineManagement;
